import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { buildApiUrl } from "../../helpers/base_url";
import "./header.css";
import { useNotification } from "./NotificationContext";
import { FiBell, FiUser } from "react-icons/fi";

function HeaderContent() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [userType, setUserType] = useState<string | null>(() => {
    try {
      const storedUser = localStorage.getItem("user");
      return storedUser ? JSON.parse(storedUser)?.userType || null : null;
    } catch {
      return null;
    }
  });
  // Check auth status immediately (synchronously) to avoid showing wrong state
  const checkAuthStatus = () => {
    try {
      const token = localStorage.getItem("accessToken");
      // Check if token exists and is not empty
      const hasValidToken = !!(token && token.trim().length > 0);
      // Clean up invalid tokens
      if (token && !hasValidToken) {
        localStorage.removeItem("accessToken");
      }
      return hasValidToken;
    } catch {
      return false;
    }
  };
  const [isLoggedIn, setIsLoggedIn] = useState(checkAuthStatus);
  const navigate = useNavigate();
  const { unreadCount } = useNotification();

  // Check if user is logged in and update on changes
  useEffect(() => {
    const checkAuth = () => {
      try {
        const token = localStorage.getItem("accessToken");
        // Check if token exists, is not empty, and is not just whitespace
        const hasValidToken = !!(token && token.trim().length > 0);
        setIsLoggedIn(hasValidToken);
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          setUserType(JSON.parse(storedUser)?.userType || null);
        } else {
          setUserType(null);
        }
      } catch (error) {
        // If localStorage is not accessible, assume not logged in
        setIsLoggedIn(false);
        setUserType(null);
      }
    };
    
    // Check auth status on mount
    checkAuth();
    
    // Listen for storage changes (e.g., when token is added/removed in another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "accessToken" || e.key === null) {
        checkAuth();
      }
    };
    
    // Also listen for custom events (for same-tab updates)
    const handleCustomStorageChange = () => {
      checkAuth();
    };
    
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("localStorageChange", handleCustomStorageChange);
    
    // Check periodically to catch any changes (every 2 seconds is reasonable)
    const interval = setInterval(checkAuth, 2000);
    
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("localStorageChange", handleCustomStorageChange);
      clearInterval(interval);
    };
  }, []);

  const toggleDropdown = (e: any) => {
    e.preventDefault();
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLogout = async () => {
    const token = localStorage.getItem("accessToken");

    try {
      if (token) {
        await fetch(buildApiUrl("/api/auth/logout"), {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
      }
    } catch (error) {
      console.error("Failed to logout from server:", error);
    } finally {
      localStorage.clear();
      setIsLoggedIn(false);
      setUserType(null);
      setIsDropdownOpen(false);
      navigate("/login");
    }
  };

  return (
    <header className="header-area clearfix transparent-bar">
      <div className="container">
        <div className=" ">
          <div className="row">
            <div className="col-lg-2 d-none d-lg-flex justify-content-center align-items-center">
              <div className=" text-center">
                <a href="/">
                  <img 
                    alt="" 
                    src="/assets/img/logo/logo-4.png"
                    style={{ 
                      maxWidth: "150px", 
                      width: "100%", 
                      height: "auto",
                      objectFit: "contain"
                    }} 
                  />
                </a>
              </div>
            </div>
            <div className="col-xl-8 col-lg-12 d-none d-lg-block">
              <div className="main-menu">
                <nav>
                  <ul
                    style={{ display: "flex", alignItems: "center", gap: 32 }}
                  >
                    <li>
                      <a href="/">Home</a>
                    </li>
                    <li>
                      <a href="/vendors">Vendors</a>
                    </li>
                    <li>
                      <a href="/about">About</a>
                    </li>
                    <li>
                      <a href="/contact">Contact</a>
                    </li>
                    {/* Right side actions */}
                    <li
                      style={{
                        marginLeft: "auto",
                        display: "flex",
                        alignItems: "center",
                        gap: 18,
                      }}
                    >
                      {/* Notifications */}
                      <a
                        href={isLoggedIn ? "/notifications" : "/login"}
                        title="Notifications"
                        style={{
                          fontSize: 22,
                          color: "#6C63FF",
                          marginRight: 8,
                          position: "relative",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "color 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "#5a52d5";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#6C63FF";
                        }}
                      >
                        <FiBell size={22} />
                        {isLoggedIn && unreadCount > 0 && (
                          <span
                            style={{
                              position: "absolute",
                              top: -6,
                              right: -6,
                              background: "#dc3545",
                              color: "#fff",
                              borderRadius: "50%",
                              fontSize: 11,
                              fontWeight: 700,
                              padding: "2px 6px",
                              minWidth: 18,
                              height: 18,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              boxShadow: "0 2px 6px rgba(220, 53, 69, 0.4)",
                              border: "2px solid #fff",
                            }}
                          >
                            {unreadCount > 9 ? "9+" : unreadCount}
                          </span>
                        )}
                      </a>
                      {/* Profile dropdown */}
                      <div style={{ position: "relative" }}>
                        <button
                          onClick={toggleDropdown}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            fontSize: 22,
                            color: "#6C63FF",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "4px",
                            borderRadius: "50%",
                            transition: "all 0.2s ease",
                          }}
                          title="Profile"
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = "#5a52d5";
                            e.currentTarget.style.background = "rgba(108, 99, 255, 0.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = "#6C63FF";
                            e.currentTarget.style.background = "none";
                          }}
                        >
                          <FiUser size={22} />
                        </button>
                        {isDropdownOpen && (
                          <div
                            style={{
                              position: "absolute",
                              right: 0,
                              top: 36,
                              background: "#fff",
                              boxShadow: "0 2px 8px #eee",
                              borderRadius: 8,
                              zIndex: 100,
                              minWidth: 140,
                            }}
                          >
                            {isLoggedIn ? (
                              <>
                                <a
                                  href="/profile"
                                  style={{
                                    display: "block",
                                    padding: "10px 18px",
                                    color: "#222",
                                    textDecoration: "none",
                                    borderBottom: "1px solid #eee",
                                  }}
                                  onClick={() => setIsDropdownOpen(false)}
                                >
                                  View Profile
                                </a>
                                <a
                                  href="/edit-profile"
                                  style={{
                                    display: "block",
                                    padding: "10px 18px",
                                    color: "#222",
                                    textDecoration: "none",
                                    borderBottom: "1px solid #eee",
                                  }}
                                  onClick={() => setIsDropdownOpen(false)}
                                >
                                  Edit Profile
                                </a>
                                <a
                                  href="/account/delete-request"
                                  style={{
                                    display: "block",
                                    padding: "10px 18px",
                                    color: "#dc3545",
                                    textDecoration: "none",
                                    borderBottom: "1px solid #eee",
                                  }}
                                  onClick={() => setIsDropdownOpen(false)}
                                >
                                  Delete Account
                                </a>
                                {userType?.toUpperCase() === "CLIENT" && (
                                  <>
                                    <a
                                      href="/support#create-case"
                                      style={{
                                        display: "block",
                                        padding: "10px 18px",
                                        color: "#222",
                                        textDecoration: "none",
                                        borderBottom: "1px solid #eee",
                                      }}
                                      onClick={() => setIsDropdownOpen(false)}
                                    >
                                      Create Support Case
                                    </a>
                                    <a
                                      href="/support#my-cases"
                                      style={{
                                        display: "block",
                                        padding: "10px 18px",
                                        color: "#222",
                                        textDecoration: "none",
                                        borderBottom: "1px solid #eee",
                                      }}
                                      onClick={() => setIsDropdownOpen(false)}
                                    >
                                      My Support Cases
                                    </a>
                                  </>
                                )}
                                <button
                                  onClick={handleLogout}
                                  style={{
                                    width: "100%",
                                    background: "none",
                                    border: "none",
                                    color: "#dc3545",
                                    padding: "10px 18px",
                                    textAlign: "left",
                                    cursor: "pointer",
                                  }}
                                >
                                  Logout
                                </button>
                              </>
                            ) : (
                              <a
                                href="/login"
                                style={{
                                  display: "block",
                                  padding: "10px 18px",
                                  color: "#222",
                                  textDecoration: "none",
                                }}
                                onClick={() => setIsDropdownOpen(false)}
                              >
                                Login
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
            <div className="header-bottom sticky-bar header-res-padding header-padding-2">
              <div className="container">
                <div className="row">
                  <div className="col-12 d-block d-lg-none">
                    <div className="logo" style={{ textAlign: "center", padding: "10px 0" }}>
                      <a href="/">
                        <img 
                          alt="" 
                          src="assets/img/logo/logo.png" 
                          style={{ 
                            maxWidth: "120px", 
                            width: "100%", 
                            height: "auto",
                            objectFit: "contain"
                          }} 
                        />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default HeaderContent;
